import { useState } from "react";
import { startCase } from "lodash";
import { Label } from "@/app/admin/_common/ui/label";
import { Input } from "@/app/admin/_common/ui/input";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import CuoButton from "@/app/admin/_common/CuoButton";
import { SHOWTYPES } from "@/app/_utils/constants";
import { ArrowRight, RotateCcw, X } from "lucide-react";
import { intakeList, type WaitlistFilters as WaitlistFiltersType } from "../../waitlistPageModel";

const showTypes = ["all", ...SHOWTYPES];

export default function WaitlistFilters({
  filters,
  onApply,
  onReset,
  onClose,
}: {
  filters: WaitlistFiltersType;
  onApply: (next: WaitlistFiltersType) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [local, setLocal] = useState<WaitlistFiltersType>(filters);

  function toggleIntake(value: number, checked: boolean) {
    setLocal((prev) => ({
      ...prev,
      intake: checked ? [...prev.intake, value] : prev.intake.filter((v) => v !== value),
    }));
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:py-2 sm:pl-3">
        <span className="text-[13px] font-normal text-foreground">Filters</span>
        <button
          type="button"
          onClick={onClose}
          className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          aria-label="Close filters"
        >
          <X className="size-3.5" />
        </button>
      </div>

      <div className="flex max-h-[60vh] flex-col gap-5 overflow-y-auto p-4 sm:max-h-[350px] sm:gap-3 sm:p-3">
        <div className="grid gap-1.5">
          <Label>Show Type</Label>
          <Select value={local.showType} onValueChange={(v) => setLocal((p) => ({ ...p, showType: v }))}>
            <SelectTrigger className="bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {showTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {startCase(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-1.5">
          <Label>No. of shows</Label>
          <Input
            type="number"
            value={local.num_of_shows}
            onChange={(e) => setLocal((p) => ({ ...p, num_of_shows: Number(e.target.value) }))}
          />
        </div>

        <div className="grid gap-1.5">
          <Label>Position</Label>
          <div className="flex items-center gap-2">
            <Input
              className="bg-white"
              type="number"
              value={local.position.start}
              onChange={(e) =>
                setLocal((p) => ({ ...p, position: { ...p.position, start: Number(e.target.value) } }))
              }
            />
            <ArrowRight className="size-3.5 flex-none text-muted-foreground" />
            <Input
              className="bg-white"
              type="number"
              value={local.position.end}
              onChange={(e) =>
                setLocal((p) => ({ ...p, position: { ...p.position, end: Number(e.target.value) } }))
              }
            />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label>Signup date range</Label>
          <div className="flex items-center gap-2">
            <Input
              className="bg-white"
              type="date"
              value={local.signup.start}
              onChange={(e) => setLocal((p) => ({ ...p, signup: { ...p.signup, start: e.target.value } }))}
            />
            <ArrowRight className="size-3.5 flex-none text-muted-foreground" />
            <Input
              className="bg-white"
              type="date"
              value={local.signup.end}
              onChange={(e) => setLocal((p) => ({ ...p, signup: { ...p.signup, end: e.target.value } }))}
            />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label>Intake</Label>
          <div className="grid grid-cols-2 gap-x-1.5 gap-y-3 sm:gap-y-1.5">
            {intakeList.map((intake) => (
              <label key={intake.value} className="flex items-center gap-2 text-[12.5px] text-foreground">
                <Checkbox
                  checked={local.intake.includes(intake.value)}
                  onCheckedChange={(checked) => toggleIntake(intake.value, !!checked)}
                />
                {intake.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border p-2">
        <CuoButton
          variant="ghost"
          icon={<RotateCcw className="size-3.5" />}
          label="Reset"
          onClick={() => {
            onReset();
          }}
        />
        <CuoButton variant="default" label="Apply Now" onClick={() => onApply(local)} />
      </div>
    </div>
  );
}
